import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { addFogDrift, getFogTexture } from '../../../shared/fogAppearance.mjs';
import { FOG_SIZE } from '../../../shared/fogMask.mjs';
import { brushCells } from '../../../shared/mapLayout.mjs';
import { smokeFogTexture } from '../utils/mapOverlay';
import './FogOfWar.scss';

// Square brush sizes, as in the Map Maker.
export const FOG_BRUSH_SIZES = [[1, '1 square'], [2, '2 × 2'], [3, '3 × 3'], [5, '5 × 5']];

export function FogControls({ fog, painting, setPainting, size, setSize, mode = 'reveal', setMode, children }) {
  const [confirm, setConfirm] = useState(false);
  const anchor = useRef(null);
  const [controlHost, setControlHost] = useState(null);
  useLayoutEffect(() => {
    // The full-screen map sits below the DM chrome. A local z-index cannot escape it.
    const surface = anchor.current?.closest('.zombies-dm-page__map-surface');
    const chrome = surface?.closest('.zombies-dm-page')?.querySelector('.zombies-dm-page__chrome');
    setControlHost(chrome || null);
  }, [fog.active, fog.canEdit]);
  if ((!fog.active || !fog.canEdit) && !children) return null;
  const controls = <div className="map-fog-controls" role="group" aria-label="Fog of war controls"
    onPointerDown={event => event.stopPropagation()} onPointerUp={event => event.stopPropagation()} onClick={event => event.stopPropagation()} onMouseDown={event => event.stopPropagation()}
    onMouseMove={event => event.stopPropagation()} onMouseUp={event => event.stopPropagation()}
    onTouchStart={event => event.stopPropagation()} onTouchMove={event => event.stopPropagation()}
    onTouchEnd={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
    {fog.active && fog.canEdit && <>
      <button type="button" aria-pressed={painting} onClick={event => {
        event.preventDefault(); event.stopPropagation();
        if (painting) { setConfirm(false); setPainting(false); }
        else setPainting(true);
        fog.flush();
      }}>{painting ? 'Done' : 'Paint fog'}</button>
      <button type="button" disabled={!fog.canUndo || fog.status !== 'Saved'} onClick={() => fog.reset('undo')}>Undo</button>
      {painting && <>
        {[['reveal', 'Reveal', 'Clear fog from the squares you paint'], ['hide', 'Hide', 'Cover the squares you paint with fog again']].map(([id, label, title]) => (
          <button key={id} type="button" aria-pressed={mode === id} title={title} onClick={() => setMode?.(id)}>{label}</button>
        ))}
        <label>Brush <select className="map-fog-size" aria-label="Fog brush size" value={size} onChange={event => setSize(Number(event.target.value))}>
          {FOG_BRUSH_SIZES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <button type="button" disabled={fog.status !== 'Saved'} onClick={() => setConfirm(true)}>Cover all</button>
        <button type="button" disabled={fog.status !== 'Saved'} onClick={() => fog.reset('reveal-all')}>Reveal all</button>
        {confirm && <span>Cover this map? <button type="button" onClick={() => { fog.reset('cover-all'); setConfirm(false); }}>Confirm cover</button><button type="button" onClick={() => setConfirm(false)}>Cancel</button></span>}
      </>}
    </>}
    {fog.active && (fog.canEdit || !fog.ready) && <span role="status">{fog.status}</span>}
    {/retry/i.test(fog.status) && <button type="button" onClick={fog.retry}>Retry fog</button>}
    {children}
  </div>;
  return <><span ref={anchor} hidden data-fog-controls-anchor="" />{controlHost ? createPortal(controls, controlHost) : controls}</>;
}

const cellKey = cell => `${cell.x},${cell.y}`;

// Built once a session. If it fails, players keep the generated clouds.
let smokeTexture;
const loadSmokeTexture = () => {
  smokeTexture ||= Promise.resolve().then(() => smokeFogTexture(FOG_SIZE, getFogTexture(FOG_SIZE))).catch(() => null);
  return smokeTexture;
};

/** The fog layer. While the DM paints, the brush reveals (or hides) whole grid squares, like the Map Maker's brush. */
export default function FogOfWar({ fog, painting, size = 1, mode = 'reveal', columns = 24, rows = 24 }) {
  const canvas = useRef(null), layer = useRef(null), pointer = useRef(null);
  const [cursor, setCursor] = useState(null);
  const [smoke, setSmoke] = useState(null);
  const editing = painting && fog.canEdit;
  const grid = { columns, rows };
  useLayoutEffect(() => {
    const node = canvas.current;
    const context = node?.getContext('2d');
    if (!context) return;
    const image = context.createImageData(FOG_SIZE, FOG_SIZE);
    if (!fog.canEdit) image.data.set(smoke || getFogTexture(FOG_SIZE));
    for (let i = 0; i < FOG_SIZE * FOG_SIZE; i++) image.data[i * 4 + 3] = fog.mask && (fog.mask[i >> 3] & (1 << (i & 7))) ? 0 : 255;
    if (!fog.canEdit) addFogDrift(image.data, FOG_SIZE, columns, getFogTexture(FOG_SIZE));
    context.putImageData(image, 0, 0);
  }, [fog.mask, fog.active, fog.canEdit, smoke, columns]);
  useEffect(() => {
    if (!fog.active || fog.canEdit) return undefined;
    let current = true;
    loadSmokeTexture().then((texture) => { if (current && texture) setSmoke(texture); });
    return () => { current = false; };
  }, [fog.active, fog.canEdit]);
  useEffect(() => {
    const node = layer.current;
    const end = () => {
      const activePointer = pointer.current;
      pointer.current = null; setCursor(null);
      if (activePointer && node?.hasPointerCapture?.(activePointer.id)) node.releasePointerCapture(activePointer.id);
      fog.flush();
    };
    window.addEventListener('blur', end);
    if (!editing) end();
    return () => {
      window.removeEventListener('blur', end);
      const activePointer = pointer.current;
      pointer.current = null;
      if (activePointer && node?.hasPointerCapture?.(activePointer.id)) node.releasePointerCapture(activePointer.id);
    };
    // Flush on tool/role changes, not on controller object recreation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  if (!fog.active) return null;
  const cellAt = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return { x: Math.min(Math.ceil(columns) - 1, Math.max(0, Math.floor((event.clientX - rect.left) / rect.width * columns))),
      y: Math.min(Math.ceil(rows) - 1, Math.max(0, Math.floor((event.clientY - rect.top) / rect.height * rows))) };
  };
  // Paint every square the brush passes over since the last pointer position, so a fast drag leaves no gaps.
  const paintTo = (stroke, cell) => {
    const steps = Math.max(Math.abs(cell.x - stroke.cell.x), Math.abs(cell.y - stroke.cell.y));
    const cells = [];
    for (let step = stroke.started ? 1 : 0; step <= steps; step++) {
      const t = steps ? step / steps : 0;
      const x = Math.round(stroke.cell.x + (cell.x - stroke.cell.x) * t), y = Math.round(stroke.cell.y + (cell.y - stroke.cell.y) * t);
      for (const square of brushCells(grid, x, y, size)) {
        if (!stroke.done.has(cellKey(square))) { stroke.done.add(cellKey(square)); cells.push(square); }
      }
    }
    if (cells.length && !fog.paint({ kind: 'cells', cells, columns, rows, hide: mode === 'hide', strokeId: stroke.strokeId })) {
      cells.forEach(square => stroke.done.delete(cellKey(square)));
      return false;
    }
    stroke.cell = cell; stroke.started = true;
    return true;
  };
  const move = event => {
    if (!editing) return;
    event.stopPropagation(); const cell = cellAt(event); if (!cell) return;
    setCursor(cell);
    if (pointer.current && pointer.current.id === event.pointerId) paintTo(pointer.current, cell);
  };
  const end = event => {
    event.stopPropagation();
    if (!pointer.current || pointer.current.id !== event.pointerId) return;
    pointer.current = null; fog.flush();
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const preview = editing && cursor ? brushCells(grid, cursor.x, cursor.y, size) : [];
  const box = preview.length ? {
    left: Math.min(...preview.map(cell => cell.x)), right: Math.max(...preview.map(cell => cell.x)),
    top: Math.min(...preview.map(cell => cell.y)), bottom: Math.max(...preview.map(cell => cell.y)),
  } : null;
  const gridLines = [...Array(Math.ceil(columns) + 1).keys()].map(x => `M${x} 0V${rows}`).join('')
    + [...Array(Math.ceil(rows) + 1).keys()].map(y => `M0 ${y}H${columns}`).join('');
  return <div ref={layer} className={`map-fog-layer${editing ? ' map-fog-layer--painting' : ''}`}
    style={{ pointerEvents: editing ? 'auto' : 'none', background: !fog.mask ? '#000' : undefined, transform: 'translate(var(--campaign-map-pan-x, 0px), var(--campaign-map-pan-y, 0px)) scale(var(--campaign-map-zoom, 1))' }} aria-label={fog.canEdit ? 'DM fog overlay' : 'Fog of war'}
    onPointerDown={event => {
      if (!editing || event.button !== 0 || pointer.current) return;
      event.preventDefault(); event.stopPropagation(); const cell = cellAt(event); if (!cell) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      const strokeId = window.crypto?.randomUUID?.() || `stroke-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const stroke = { id: event.pointerId, cell, strokeId, done: new Set(), started: false };
      if (paintTo(stroke, cell)) pointer.current = stroke;
      setCursor(cell);
    }} onPointerMove={move} onPointerUp={event => { move(event); end(event); }} onPointerCancel={end} onLostPointerCapture={end}
    onPointerLeave={() => { if (!pointer.current) setCursor(null); }}
    onWheel={event => { if (pointer.current) event.stopPropagation(); }}
    onTouchStart={event => event.stopPropagation()} onTouchMove={event => event.stopPropagation()}>
    <canvas ref={canvas} width={FOG_SIZE} height={FOG_SIZE} style={{ opacity: fog.canEdit ? .6 : 1 }} />
    {editing && <svg className="map-fog-grid" viewBox={`0 0 ${columns} ${rows}`} preserveAspectRatio="none" aria-hidden="true"><path d={gridLines} /></svg>}
    {box && <span className={`map-fog-brush map-fog-brush--${mode}`} data-brush-squares={preview.length}
      style={{ left: `${box.left / columns * 100}%`, top: `${box.top / rows * 100}%`,
        width: `${(box.right - box.left + 1) / columns * 100}%`, height: `${(box.bottom - box.top + 1) / rows * 100}%` }} />}
  </div>;
}
